import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { StartStreamingScreen } from '../components/Interior/StartStreamingScreen';
import { LandingScreen } from '../components/Interior/LandingScreen';
import { DesignQuestionScreen } from '../components/Interior/DesignQuestionScreen';
import { EmailScreen } from '../components/Interior/EmailScreen';
import { UserIdScreen } from '../components/Interior/UserIdScreen';
import { LoadingScreen } from '../components/Interior/LoadingScreen';
import { PressAnyKeyScreen } from '../components/Interior/PressAnyKeyScreen';
import { PreferenceScreen } from '../components/Interior/PreferenceScreen';
import { InteriorViewer } from '../components/Interior/InteriorViewer';
import { ModelSelectionScreen } from '../components/Interior/ModelSelectionScreen';
import { interiorModels } from '../data/interiorMockData';
import { ThemeProvider } from '../components/styles/ThemeContext';
type InteriorScreen = 'landing' | 'question' | 'email' | 'userId' | 'models' | 'loading' | 'pressKey' | 'preference';
function InteriorAppContent() {
  const [currentScreen, setCurrentScreen] = useState<InteriorScreen>('landing');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [fromEmail, setFromEmail] = useState(false);
  // Route the current URL path to the correct screen
  useEffect(() => {
    const resolveScreenFromPath = (): InteriorScreen => {
      const path = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      // Legacy skip parameter support
      if (urlParams.get('skip') === 'true') {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        return 'preference';
      }
      if (path === '/interior/question') return 'question';
      if (path === '/interior/email') return 'email';
      if (path === '/interior/login') return 'userId';
      if (path === '/interior/models') return 'models';
      if (path === '/interior/loading') return 'loading';
      if (path === '/interior/ready') return 'pressKey';
      if (path === '/interior/preference') return 'preference';
      // Default: /interior → landing
      return 'landing';
    };
    setCurrentScreen(resolveScreenFromPath());
    // Handle browser back/forward buttons
    const handlePopState = () => {
      setCurrentScreen(resolveScreenFromPath());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const handleStartStreaming = () => {
    setCurrentScreen('landing');
    window.history.pushState({}, '', '/interior');
  };
  const handleBeginTour = () => {
    setCurrentScreen('question');
    window.history.pushState({}, '', '/interior/question');
  };
  const handleDesignYes = () => {
    setFromEmail(false);
    setCurrentScreen('userId');
    window.history.pushState({}, '', '/interior/login');
  };
  const handleDesignNo = () => {
    setFromEmail(true);
    setCurrentScreen('email');
    window.history.pushState({}, '', '/interior/email');
  };
  const handleEmailSubmit = (email: string) => {
    setUserEmail(email);
    setCurrentScreen('userId');
    window.history.pushState({}, '', '/interior/login');
  };
  const handleUserIdSubmit = (id: string) => {
    setUserId(id);
    setCurrentScreen('models');
    window.history.pushState({}, '', '/interior/models');
  };
  const handleModelSelect = (modelId: string) => {
    setSelectedModelId(modelId);
    setCurrentScreen('loading');
    window.history.pushState({}, '', '/interior/loading');
  };
  const handleLoadingComplete = () => {
    setCurrentScreen('pressKey');
    window.history.pushState({}, '', '/interior/ready');
  };
  const handlePressKey = () => {
    setCurrentScreen('preference');
    window.history.pushState({}, '', '/interior/preference');
  };
  const handleSelectGuidedTour = () => {
    window.open('https://app-visualizer.resvr.com?mode=guided', '_blank');
  };
  const handleSelectMouseKeyboard = () => {
    window.open('https://app-visualizer.resvr.com?mode=manual', '_blank');
  };
  const handleBack = () => {
    if (currentScreen === 'question') {
      setCurrentScreen('landing');
    } else if (currentScreen === 'email') {
      setCurrentScreen('question');
    } else if (currentScreen === 'userId') {
      if (fromEmail) {
        setCurrentScreen('email');
      } else {
        setCurrentScreen('question');
      }
    } else if (currentScreen === 'models') {
      setCurrentScreen('userId');
    }
  };
  const handleExit = () => {
    window.history.pushState({}, '', '/');
    window.location.href = '/';
  };
  return <div className="relative w-full h-screen overflow-hidden" data-id="element-1213">
      <AnimatePresence mode="wait" data-id="element-1214">
        {currentScreen === 'streaming' && <motion.div key="streaming" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1215">
            <StartStreamingScreen onStart={handleStartStreaming} data-id="element-1216" />
          </motion.div>}

        {currentScreen === 'landing' && <motion.div key="landing" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1217">
            <LandingScreen onBeginTour={handleBeginTour} onExit={handleExit} data-id="element-1218" />
          </motion.div>}

        {currentScreen === 'question' && <motion.div key="question" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1219">
            <DesignQuestionScreen onYes={handleDesignYes} onNo={handleDesignNo} onBack={handleBack} data-id="element-1220" />
          </motion.div>}

        {currentScreen === 'email' && <motion.div key="email" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1221">
            <EmailScreen onSubmit={handleEmailSubmit} onBack={handleBack} data-id="element-1222" />
          </motion.div>}

        {currentScreen === 'userId' && <motion.div key="userId" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1223">
            <UserIdScreen onSubmit={handleUserIdSubmit} onBack={handleBack} fromEmail={fromEmail} data-id="element-1224" />
          </motion.div>}

        {currentScreen === 'models' && <motion.div key="models" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1225">
            <ModelSelectionScreen models={interiorModels} onSelectModel={handleModelSelect} onBack={handleBack} data-id="element-1226" />
          </motion.div>}

        {currentScreen === 'loading' && <motion.div key="loading" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1227">
            <LoadingScreen onComplete={handleLoadingComplete} data-id="element-1228" />
          </motion.div>}

        {currentScreen === 'pressKey' && <motion.div key="pressKey" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1229">
            <PressAnyKeyScreen onContinue={handlePressKey} data-id="element-1230" />
          </motion.div>}

        {currentScreen === 'preference' && <motion.div key="preference" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1231">
            <PreferenceScreen onSelectGuidedTour={handleSelectGuidedTour} onSelectMouseKeyboard={handleSelectMouseKeyboard} data-id="element-1232" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function InteriorApp() {
  return <ThemeProvider data-id="element-1233">
      <InteriorAppContent data-id="element-1234" />
    </ThemeProvider>;
}